import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { API_BASE_URL, ACTION_API_BASE_URL } from '../config/api';
import { clearCompanySessionCache, syncCompanySessionCache } from '../utils/companySessionCache';
import { parseLoginModules } from '../utils/loginModuleMenus';
import { invalidateMyGoalCache } from '../utils/myGoalCache';
import { isWorkspaceSelectable } from '../components/auth/WorkspacePicker';

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // بارگذاری توکن از localStorage
    return localStorage.getItem('authToken');
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // بررسی وجود توکن
    return !!localStorage.getItem('authToken');
  });
  const [loading, setLoading] = useState(false);

  // توکن و اطلاعات کاربر ماژول action
  const [actionToken, setActionToken] = useState(() => {
    return localStorage.getItem('actionToken');
  });
  const [actionUser, setActionUser] = useState(() => {
    const saved = localStorage.getItem('actionUserData');
    return saved ? JSON.parse(saved) : null;
  });

  // بارگذاری user از localStorage در صورت وجود
  useEffect(() => {
    const savedUser = localStorage.getItem('userData');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('userData');
      }
    }
  }, []);

  // ذخیره توکن و user در localStorage
  useEffect(() => {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('userData', JSON.stringify(user));
    } else {
      localStorage.removeItem('userData');
    }
  }, [user]);

  // منوهای مجاز فقط هنگام لاگین ساخته می‌شدند؛ با تغییر دسترسی‌ها (تنظیمات نقش‌ها) تا لاگین بعدی کهنه می‌ماندند.
  // اینجا هنگام باز شدن اپ و بازگشت به تب (حداکثر هر ۶۰ ثانیه) از allowed-menus تازه می‌شوند.
  const menusRefreshedAtRef = useRef(0);
  useEffect(() => {
    if (!token || !user?.id) return undefined;

    const refreshMenus = async () => {
      const now = Date.now();
      if (now - menusRefreshedAtRef.current < 60000) return;
      menusRefreshedAtRef.current = now;

      try {
        const response = await fetch(`${API_BASE_URL}/allowed-menus`, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const body = await response.json();
        const allowed = body?.data?.allowed_menus;
        if (!allowed || typeof allowed !== 'object' || Array.isArray(allowed) || !Object.keys(allowed).length) return;

        const { moduleMenus, activeModules } = parseLoginModules(allowed);
        setUser((prev) => {
          if (!prev || JSON.stringify(prev.moduleMenus) === JSON.stringify(moduleMenus)) return prev;
          return { ...prev, moduleMenus, modules: activeModules };
        });
      } catch (error) {
        // بدون پیام: در صورت خطا همان منوهای زمان لاگین می‌مانند
        console.warn('Menu refresh failed:', error);
      }
    };

    refreshMenus();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshMenus();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [token, user?.id]);

  // ذخیره توکن و اطلاعات کاربر ماژول action در localStorage
  useEffect(() => {
    if (actionToken) {
      localStorage.setItem('actionToken', actionToken);
    } else {
      localStorage.removeItem('actionToken');
    }
  }, [actionToken]);

  useEffect(() => {
    if (actionUser) {
      localStorage.setItem('actionUserData', JSON.stringify(actionUser));
    } else {
      localStorage.removeItem('actionUserData');
    }
  }, [actionUser]);

  const buildUserObject = (userData, loginModulesPayload = null, companyModules = null) => {
    const modulesSource =
      loginModulesPayload != null
        ? loginModulesPayload
        : userData.moduleMenus != null
          ? userData.moduleMenus
          : userData.modules;

    const { moduleMenus, activeModules } = parseLoginModules(modulesSource);

    return {
      id: userData.id,
      company_id: userData.company_id,
      user_auth_id: userData.user_auth_id,
      is_superadmin: userData.is_superadmin || 0,
      name: userData.name,
      email: userData.email,
      image: userData.image,
      image_url: userData.image_url,
      mobile: userData.mobile,
      gender: userData.gender,
      locale: userData.locale || 'fa',
      status: userData.status,
      login: userData.login,
      dark_theme: userData.dark_theme || 0,
      modules: activeModules,
      moduleMenus,
      /** ماژول‌های اختصاصی شرکت (مثل chaproom) از پاسخ لاگین — مستقل از نقش کاربر */
      company_modules: Array.isArray(companyModules)
        ? companyModules
        : Array.isArray(userData.company_modules)
          ? userData.company_modules
          : null,
      roles: userData.roles || [],
    };
  };

  const applyAuthSession = (authToken, userData, loginModulesPayload = null, companyModules = null) => {
    const userObject = buildUserObject(userData, loginModulesPayload, companyModules);
    // بلافاصله در localStorage — قبل از رندر فرزندان که API می‌زنند
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('userData', JSON.stringify(userObject));
    syncCompanySessionCache(userObject.company_id);
    window.__AUTH_REDIRECTING__ = false;
    // کش هدف کاربر قبلی را پاک کن تا بعد از لاگین my-goal تازه گرفته شود
    invalidateMyGoalCache();
    setToken(authToken);
    setUser(userObject);
    setIsAuthenticated(true);
    return { success: true, user: userObject, message: 'ورود با موفقیت انجام شد' };
  };

  /** کلیدهای i18n بک‌اند — پیام خام نمایش داده نشود */
  const normalizeAuthMessage = (msg) => {
    if (!msg || typeof msg !== 'string') return null;
    if (msg.startsWith('messages.')) {
      const known = {
        'messages.workspaceSelectionRequired': null,
      };
      if (known[msg] === null) return null;
      return known[msg] ?? null;
    }
    return msg;
  };

  const parseAuthResponse = (data, response) => {
    const payload = data?.data;

    // یک شرکت: توکن مستقیم — بدون انتخاب workspace
    if (payload?.token && payload?.user) {
      return applyAuthSession(
        payload.token,
        payload.user,
        payload.modules ?? null,
        payload.company_modules ?? null,
      );
    }

    // چند شرکت: نمایش لیست برای انتخاب پنل
    if (payload?.requires_workspace_selection === true && Array.isArray(payload.workspaces)) {
      return {
        success: false,
        requiresWorkspaceSelection: true,
        workspaces: payload.workspaces,
        message: normalizeAuthMessage(data?.message),
      };
    }

    if (!response.ok) {
      return {
        success: false,
        error: normalizeAuthMessage(data?.message) || 'ایمیل یا رمز عبور اشتباه است',
      };
    }

    return { success: false, error: normalizeAuthMessage(data?.message) || 'فرمت response نامعتبر است' };
  };

  const postAuthJson = async (path, body) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    try {
      const data = await response.json();
      return { data, response };
    } catch {
      return { data: null, response };
    }
  };

  // تابع لاگین
  const login = async (credentials) => {
    setLoading(true);
    try {
      const { data, response } = await postAuthJson('/auth/login', {
        email: credentials.email,
        password: credentials.password,
      });

      if (!data) {
        return { success: false, error: 'ایمیل یا رمز عبور اشتباه است' };
      }

      const parsed = parseAuthResponse(data, response);

      // اگر کاربر در چند شرکت است ولی فقط در یک شرکت فعال است، مستقیم وارد همان شرکت شود
      if (parsed.requiresWorkspaceSelection && Array.isArray(parsed.workspaces)) {
        const selectableWorkspaces = parsed.workspaces.filter((ws) => isWorkspaceSelectable(ws));
        if (selectableWorkspaces.length === 1) {
          const singleWs = selectableWorkspaces[0];
          return await chooseWorkspace({
            email: credentials.email,
            password: credentials.password,
            user_id: singleWs.user_id,
            company_id: singleWs.company_id,
          });
        }
        if (selectableWorkspaces.length === 0) {
          return {
            success: false,
            error: 'حساب کاربری شما در هیچ‌یک از شرکت‌ها فعال نیست',
          };
        }
      }

      return parsed;
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'ایمیل یا رمز عبور اشتباه است' };
    } finally {
      setLoading(false);
    }
  };

  /** انتخاب workspace پس از لاگین چندشرکتی */
  const chooseWorkspace = async ({ email, password, user_id, company_id }) => {
    setLoading(true);
    try {
      const { data, response } = await postAuthJson('/auth/choose-workspace', {
        email,
        password,
        user_id,
        company_id,
      });

      if (!data) {
        return { success: false, error: 'خطا در ارتباط با سرور' };
      }

      return parseAuthResponse(data, response);
    } catch (error) {
      console.error('Choose workspace error:', error);
      return { success: false, error: 'خطا در ورود به پنل انتخاب‌شده' };
    } finally {
      setLoading(false);
    }
  };

  // تابع لاگ‌اوت
  const logout = async () => {
    try {
      // ارسال درخواست logout به API
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }).catch(error => {
          // اگر درخواست logout با خطا مواجه شد، باز هم logout را انجام می‌دهیم
          console.error('Logout API error:', error);
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // در هر صورت، state و localStorage را پاک می‌کنیم
      invalidateMyGoalCache();
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      clearCompanySessionCache();
    }
  };

  // تابع بررسی اعتبار توکن
  const checkAuth = async () => {
    if (!token) {
      setIsAuthenticated(false);
      return false;
    }

    try {
      // TODO: اینجا باید API call برای بررسی اعتبار توکن انجام شود
      // const response = await fetch('/api/auth/verify', {
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //   },
      // });
      // if (response.ok) {
      //   const data = await response.json();
      //   setUser(data.user);
      //   return true;
      // } else {
      //   logout();
      //   return false;
      // }

      // برای تست
      return true;
    } catch (error) {
      console.error('Auth check error:', error);
      logout();
      return false;
    }
  };

  // تابع برای API calls با Bearer Token
  const apiCall = async (url, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // تابع لاگین ماژول action
  const loginActionModule = async () => {
    if (!token) {
      return { 
        success: false, 
        error: 'ابتدا باید به سیستم اصلی وارد شوید' 
      };
    }

    setLoading(true);
    try {
      // endpoint لاگین ماژول action
      const response = await fetch(`${ACTION_API_BASE_URL}/integration/erp/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          erp_token: token,
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        return { 
          success: false, 
          error: 'خطا در دریافت پاسخ از سرور' 
        };
      }

      if (!response.ok) {
        return { 
          success: false, 
          error: data.message || 'خطا در احراز هویت ماژول اقدام' 
        };
      }

      if (data.success && data.data && data.data.token) {
        const { token: newToken, user: userData } = data.data;
        setActionToken(newToken);
        setActionUser(userData);
        
        return { 
          success: true, 
          message: 'ورود به ماژول اقدام با موفقیت انجام شد',
          data: data.data
        };
      } else {
        return { 
          success: false, 
          error: 'فرمت response نامعتبر است' 
        };
      }
    } catch (error) {
      console.error('Action module login error:', error);
      return { 
        success: false, 
        error: 'خطا در ارتباط با سرور' 
      };
    } finally {
      setLoading(false);
    }
  };

  // تابع لاگ‌اوت ماژول action
  const logoutActionModule = () => {
    setActionToken(null);
    setActionUser(null);
    localStorage.removeItem('actionToken');
    localStorage.removeItem('actionUserData');
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    chooseWorkspace,
    logout,
    checkAuth,
    apiCall,
    // ماژول action
    actionToken,
    actionUser,
    loginActionModule,
    logoutActionModule,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

